import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Sparkles, RefreshCw, FileText, Download, ChevronRight, Upload, Trash2, MailIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  adminAnalyzeApplication, fetchResumeBlob,
  adminListResumeUploads, adminDeleteResumeUpload, adminAnalyzeResumeUpload, fetchUploadedResumeBlob,
} from '../../api';
import { useToast } from '../../hooks/use-toast';
import AnalysisPanel from './AnalysisPanel';
import UploadResumeModal from './UploadResumeModal';

const scoreColor = (score) => {
  if (score >= 80) return 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30';
  if (score >= 60) return 'bg-amber-400/15 text-amber-300 border-amber-400/30';
  return 'bg-red-400/15 text-red-300 border-red-400/30';
};

const AnalysisTab = ({ apps, loading }) => {
  const { toast } = useToast();
  const [analyses, setAnalyses] = useState({});
  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  const loadUploads = useCallback(async () => {
    try { setUploads(await adminListResumeUploads()); }
    catch (err) { console.warn('load uploads failed', err?.message); }
    finally { setUploadsLoading(false); }
  }, []);

  useEffect(() => { loadUploads(); }, [loadUploads]);

  // Hydrate analyses from app props
  useEffect(() => {
    const map = {};
    apps.forEach(a => { if (a.analysis) map[`app:${a.id}`] = a.analysis; });
    uploads.forEach(u => { if (u.analysis) map[`upload:${u.id}`] = u.analysis; });
    setAnalyses(prev => ({ ...map, ...prev }));
  }, [apps, uploads]);

  // Build unified list
  const items = [
    ...apps.filter(a => a.resume_filename).map(a => ({
      key: `app:${a.id}`, kind: 'application', id: a.id,
      name: a.name, email: a.email, job_title: a.job_title,
      resume_filename: a.resume_filename, created_at: a.created_at,
    })),
    ...uploads.map(u => ({
      key: `upload:${u.id}`, kind: 'upload', id: u.id,
      name: u.candidate_name, email: u.candidate_email,
      job_title: u.target_job_title, resume_filename: u.resume_filename,
      created_at: u.created_at, uploaded_by: u.uploaded_by,
    })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filtered = items.filter(it => !search ||
    it.name?.toLowerCase().includes(search.toLowerCase()) ||
    (it.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (it.job_title || '').toLowerCase().includes(search.toLowerCase())
  );

  const selected = filtered.find(it => it.key === selectedKey);
  const selectedAnalysis = selected ? analyses[selected.key] : null;

  const runAnalysis = async (it) => {
    setBusyKey(it.key);
    try {
      const data = it.kind === 'upload'
        ? await adminAnalyzeResumeUpload(it.id)
        : await adminAnalyzeApplication(it.id);
      setAnalyses(prev => ({ ...prev, [it.key]: data }));
      setSelectedKey(it.key);
      toast({ title: 'Analysis complete', description: `${it.name}: ${data.result?.score}/100` });
    } catch (err) {
      toast({ title: 'Analysis failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusyKey(null); }
  };

  const removeUpload = async (it) => {
    if (!window.confirm(`Delete uploaded resume for ${it.name}? Analysis will be removed too.`)) return;
    try {
      await adminDeleteResumeUpload(it.id);
      setUploads(prev => prev.filter(u => u.id !== it.id));
      if (selectedKey === it.key) setSelectedKey(null);
      toast({ title: 'Resume deleted' });
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const downloadResume = (it) => {
    if (it.kind === 'upload') fetchUploadedResumeBlob(it.id, it.resume_filename);
    else fetchResumeBlob(it.id, it.resume_filename);
  };

  const onUploadCreated = (createdList) => {
    const arr = Array.isArray(createdList) ? createdList : [createdList];
    if (arr.length === 0) return;
    setUploads(prev => [...arr, ...prev]);
    setSelectedKey(`upload:${arr[0].id}`);
  };

  const isLoading = loading || uploadsLoading;

  return (
    <div className="grid lg:grid-cols-[380px,1fr] gap-4 min-h-[600px]">
      <div className="rounded-xl border border-cyan-400/10 bg-slate-900/30 overflow-hidden flex flex-col">
        <div className="p-3 border-b border-cyan-400/10 space-y-2">
          <Button onClick={() => setUploadOpen(true)} className="w-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold rounded-md h-9">
            <Upload className="w-4 h-4 mr-1" /> Upload Resume
          </Button>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or job..." className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white h-9 text-sm" />
        </div>
        <div className="overflow-y-auto max-h-[700px]">
          {isLoading ? (
            <div className="p-6 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">{items.length === 0 ? 'No resumes yet. Click "Upload Resume" to add one.' : 'No matches.'}</div>
          ) : filtered.map(it => {
            const score = analyses[it.key]?.result?.score;
            const isSelected = it.key === selectedKey;
            return (
              <button
                key={it.key}
                onClick={() => setSelectedKey(it.key)}
                className={`w-full text-left px-4 py-3 border-b border-cyan-400/5 transition flex items-start gap-3 ${isSelected ? 'bg-cyan-400/5' : 'hover:bg-slate-800/40'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="text-sm text-white font-medium truncate">{it.name}</p>
                    {score !== undefined && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${scoreColor(score)} flex-shrink-0`}>{score}</span>}
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wider ${it.kind === 'upload' ? 'bg-purple-400/10 text-purple-300 border border-purple-400/20' : 'bg-slate-700/40 text-slate-400 border border-slate-600/30'}`}>
                      {it.kind === 'upload' ? 'Uploaded' : 'Applied'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 truncate">{it.job_title}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{new Date(it.created_at).toLocaleDateString()}</p>
                </div>
                <ChevronRight className={`w-4 h-4 text-slate-600 mt-1 flex-shrink-0 transition ${isSelected ? 'text-cyan-400 translate-x-0.5' : ''}`} />
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-cyan-400/10 bg-slate-900/30 p-6">
        {!selected ? (
          <div className="h-full flex items-center justify-center text-center text-slate-500 py-16">
            <div>
              <Sparkles className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-sm">Upload a resume or select a candidate from the list to run AI fit analysis.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pb-4 border-b border-cyan-400/10">
              <div className="min-w-0">
                <h3 className="text-xl font-semibold text-white">{selected.name}</h3>
                {selected.email && (
                  <p className="text-sm text-slate-400 mt-1 inline-flex items-center gap-1.5">
                    <MailIcon className="w-3.5 h-3.5" />
                    <a href={`mailto:${selected.email}`} className="text-cyan-300 hover:underline">{selected.email}</a>
                  </p>
                )}
                <p data-testid="analysis-compared-to" className="text-sm text-slate-400 mt-1">Compared to: <span className="text-slate-200">{selected.job_title || 'No job linked'}</span></p>
                <p className="text-xs text-slate-500 mt-1">
                  {selected.kind === 'upload' ? 'Uploaded' : 'Applied'} {new Date(selected.created_at).toLocaleString()}
                  {selected.uploaded_by && <span> by {selected.uploaded_by}</span>}
                </p>
              </div>
              <div className="flex flex-col sm:items-end gap-2">
                <div className="flex gap-2">
                  <Button onClick={() => downloadResume(selected)} variant="outline" className="h-9 border-cyan-400/40 bg-transparent hover:bg-cyan-400/10 text-cyan-300">
                    <Download className="w-3.5 h-3.5 mr-1" /> <span className="truncate max-w-[160px]">{selected.resume_filename}</span>
                  </Button>
                  {selected.kind === 'upload' && (
                    <Button onClick={() => removeUpload(selected)} variant="outline" className="h-9 border-red-900/40 bg-transparent hover:bg-red-950/40 text-red-300" title="Delete uploaded resume">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                <Button data-testid="analysis-run-btn" onClick={() => runAnalysis(selected)} disabled={busyKey === selected.key || !selected.job_title} title={selected.job_title ? undefined : 'Link this resume to a job to analyze it'} className="bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 font-semibold rounded-md h-9 px-4">
                  {busyKey === selected.key ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>) : selectedAnalysis ? (<><RefreshCw className="w-4 h-4 mr-2" /> Re-analyze</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Analyze with AI</>)}
                </Button>
              </div>
            </div>

            {selectedAnalysis ? (
              <AnalysisPanel analysis={selectedAnalysis} />
            ) : (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm">{selected.job_title
                  ? <>No analysis yet. Click <span className="text-cyan-300 font-medium">Analyze with AI</span> to score this resume against the job posting and check other open roles for better fits.</>
                  : <span data-testid="analysis-no-job-hint">This resume isn't linked to a job, so there's nothing to score it against. Find it anytime in the Resume Repository.</span>}</p>
                <p className="text-xs text-slate-500 mt-3">Powered by Claude Sonnet 4.6 &middot; typically completes in 10-20 seconds</p>
              </div>
            )}
          </div>
        )}
      </div>

      <UploadResumeModal open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={onUploadCreated} />
    </div>
  );
};

export default AnalysisTab;
